'use client';

import React, { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
    ArrowRight,
    ArrowUp,
    Bot,
    Building2,
    CirclePlay,
    Code2,
    Dumbbell,
    LayoutGrid,
    Hammer,
    HeartPulse,
    Leaf,
    ListFilter,
    Megaphone,
    Monitor,
    Palette,
    Search,
    ShoppingBag,
    Sparkles,
    Target,
    TrendingUp,
    Wrench,
    X,
} from 'lucide-react';

type ProjectDisplayIconComponent = React.ComponentType<{
    className?: string;
    size?: number;
    style?: CSSProperties;
    strokeWidth?: number;
    'aria-hidden'?: boolean;
}>;

export type ProjectDisplayIconRenderer = React.ElementType<{
    className?: string;
    style?: CSSProperties;
    'aria-hidden'?: boolean;
    focusable?: boolean;
}>;

const iconRegistry = {
    arrowRight: ArrowRight,
    automation: Bot,
    building: Building2,
    code: Code2,
    construction: Hammer,
    ecommerce: ShoppingBag,
    fitness: Dumbbell,
    grid: LayoutGrid,
    healthcare: HeartPulse,
    landscaping: Leaf,
    marketing: Megaphone,
    play: CirclePlay,
    palette: Palette,
    plumbing: Wrench,
    website: Monitor,
    search: Search,
    sparkles: Sparkles,
    target: Target,
    trendingUp: TrendingUp,
} satisfies Record<string, ProjectDisplayIconComponent>;

export type ProjectDisplayIconToken = keyof typeof iconRegistry;

export const projectDisplayIconTokens = Object.keys(iconRegistry) as ProjectDisplayIconToken[];

export type ProjectDisplayLinkComponent = React.ComponentType<{
    href: string;
    children?: React.ReactNode;
    className?: string;
    title?: string;
    'aria-label'?: string;
    [key: string]: any;
}>;

export type ProjectDisplayLayout = 'slide' | 'stack';

export type ProjectDisplayResponsiveMode = 'slide' | 'stack';

export type ProjectDisplayFilterId = string | number;

export type ProjectDisplayFilter = {
    id: ProjectDisplayFilterId;
    label: React.ReactNode;
    count?: React.ReactNode;
    ariaLabel?: string;
    icon?: React.ReactNode;
    iconComponent?: ProjectDisplayIconRenderer;
    iconToken?: ProjectDisplayIconToken;
    accentColor?: string;
    accentColour?: string;
};

export type ProjectDisplayImage = {
    src?: string;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    originalAlt?: string;
    thumbnailAlt?: string;
    objectPosition?: string;
};

export type ProjectDisplayCategory = {
    label: React.ReactNode;
    icon?: React.ReactNode;
    iconToken?: ProjectDisplayIconToken;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type ProjectDisplayServiceChip = {
    label: React.ReactNode;
    ariaLabel?: string;
    accentColor?: string;
    accentColour?: string;
    color?: string;
    colour?: string;
    textColor?: string;
    textColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
};

export type ProjectDisplayService = React.ReactNode | ProjectDisplayServiceChip;

export type ProjectDisplayMetric = {
    value: React.ReactNode;
    label?: React.ReactNode;
    ariaLabel?: string;
};

export type ProjectDisplaySticker = {
    icon?: React.ReactNode;
    iconComponent?: ProjectDisplayIconRenderer;
    iconToken?: ProjectDisplayIconToken;
    ariaLabel?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    color?: string;
    colour?: string;
};

export type ProjectDisplayItem = {
    id: string | number;
    title: React.ReactNode;
    href: string;
    searchText?: string;
    filterId?: ProjectDisplayFilterId;
    filterIds?: ProjectDisplayFilterId[];
    description?: React.ReactNode;
    image?: ProjectDisplayImage;
    imageUrl?: string;
    imageAlt?: string;
    imagePosition?: string;
    subtitle?: React.ReactNode;
    category?: ProjectDisplayCategory;
    categoryLabel?: React.ReactNode;
    categoryIcon?: React.ReactNode;
    categoryIconToken?: ProjectDisplayIconToken;
    tags?: ProjectDisplayService[];
    services?: ProjectDisplayService[];
    metric?: ProjectDisplayMetric;
    stat?: ProjectDisplayMetric;
    sticker?: ProjectDisplaySticker | null;
    overlaySticker?: ProjectDisplaySticker | null;
    overlayIcon?: React.ReactNode;
    overlayIconComponent?: ProjectDisplayIconRenderer;
    overlayIconToken?: ProjectDisplayIconToken;
    outcome?: React.ReactNode;
    outcomeLabel?: React.ReactNode | null;
    ctaLabel?: React.ReactNode;
    ctaAriaLabel?: string;
    inverse?: boolean;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
};

export type ProjectDisplayColorToken =
    | 'background'
    | 'surface'
    | 'text'
    | 'textSoft'
    | 'textMuted'
    | 'accent'
    | 'accentSoft'
    | 'border'
    | 'sectionBorder'
    | 'chipBackground'
    | 'imageFallback'
    | 'metricSurface'
    | 'shadow'
    | 'hoverShadow';

export type ProjectDisplayColors = Partial<Record<ProjectDisplayColorToken, string>>;

export type ProjectDisplayProps = {
    items: ProjectDisplayItem[];
    Link?: ProjectDisplayLinkComponent;
    layout?: ProjectDisplayLayout;
    ResponsiveMode?: ProjectDisplayResponsiveMode;
    responsiveMode?: ProjectDisplayResponsiveMode;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    servicesLabel?: React.ReactNode;
    outcomeLabel?: React.ReactNode | null;
    ctaLabel?: React.ReactNode;
    actionHref?: string;
    actionLabel?: React.ReactNode;
    actionAriaLabel?: string;
    actionIcon?: React.ReactNode;
    actionIconComponent?: ProjectDisplayIconRenderer;
    actionIconToken?: ProjectDisplayIconToken;
    filters?: ProjectDisplayFilter[];
    showFilters?: boolean;
    defaultFilterId?: ProjectDisplayFilterId;
    allFilterLabel?: React.ReactNode;
    allFilterCount?: React.ReactNode;
    allFilterIcon?: React.ReactNode;
    allFilterIconComponent?: ProjectDisplayIconRenderer;
    allFilterIconToken?: ProjectDisplayIconToken;
    filterAriaLabel?: string;
    stickyFilters?: boolean;
    stickyFilterTop?: number | string;
    showTextFilter?: boolean;
    textFilterPlaceholder?: string;
    textFilterAriaLabel?: string;
    clearTextFilterAriaLabel?: string;
    mobileFilterShortcutAriaLabel?: string;
    emptyMessage?: React.ReactNode;
    colors?: ProjectDisplayColors;
    colours?: ProjectDisplayColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    tileWidth?: number | string;
    minTileWidth?: number | string;
    tileGap?: number | string;
    imageHeight?: number | string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type ProjectDisplayStyle = CSSProperties &
    Record<`--project-display-${string}`, string | number | undefined>;

type ProjectDisplayItemStyle = CSSProperties &
    Record<`--project-display-${string}`, string | number | undefined>;

const projectDisplayInverseColors = {
    text: '#ffffff',
    accentSoft: 'rgba(255, 255, 255, 0.16)',
    chipBackground: 'rgba(255, 255, 255, 0.18)',
    stickerBackground: 'rgba(255, 255, 255, 0.96)',
} as const;

export const projectDisplayTokens = {
    colors: {
        background: '#fbfef8',
        surface: '#ffffff',
        text: '#080b1d',
        textSoft: '#475467',
        textMuted: '#667085',
        accent: '#16a329',
        accentSoft: 'rgba(22, 163, 41, 0.1)',
        border: 'rgba(16, 24, 40, 0.1)',
        sectionBorder: 'rgba(22, 163, 41, 0.16)',
        chipBackground: 'rgba(16, 24, 40, 0.06)',
        imageFallback: '#f1f5f2',
        metricSurface: '#ffffff',
        shadow: '0 12px 30px rgba(16, 24, 40, 0.07)',
        hoverShadow: '0 18px 44px rgba(16, 24, 40, 0.13)',
    },
    sizes: {
        maxWidth: 1440,
        tileWidth: 300,
        minTileWidth: 272,
        tileGap: 16,
        imageHeight: 150,
    },
} as const;

const DefaultLink: ProjectDisplayLinkComponent = ({ href, children, ...props }) => (
    <a href={href} {...props}>
        {children}
    </a>
);

const projectDisplayCss = `
.project-display {
  --project-display-bg: ${projectDisplayTokens.colors.background};
  --project-display-surface: ${projectDisplayTokens.colors.surface};
  --project-display-text: ${projectDisplayTokens.colors.text};
  --project-display-text-soft: ${projectDisplayTokens.colors.textSoft};
  --project-display-text-muted: ${projectDisplayTokens.colors.textMuted};
  --project-display-accent: ${projectDisplayTokens.colors.accent};
  --project-display-accent-soft: ${projectDisplayTokens.colors.accentSoft};
  --project-display-border: ${projectDisplayTokens.colors.border};
  --project-display-section-border: ${projectDisplayTokens.colors.sectionBorder};
  --project-display-chip-bg: ${projectDisplayTokens.colors.chipBackground};
  --project-display-image-fallback: ${projectDisplayTokens.colors.imageFallback};
  --project-display-metric-surface: ${projectDisplayTokens.colors.metricSurface};
  --project-display-shadow: ${projectDisplayTokens.colors.shadow};
  --project-display-hover-shadow: ${projectDisplayTokens.colors.hoverShadow};
  --project-display-max-width: ${projectDisplayTokens.sizes.maxWidth}px;
  --project-display-tile-width: ${projectDisplayTokens.sizes.tileWidth}px;
  --project-display-min-tile-width: ${projectDisplayTokens.sizes.minTileWidth}px;
  --project-display-tile-gap: ${projectDisplayTokens.sizes.tileGap}px;
  --project-display-image-height: ${projectDisplayTokens.sizes.imageHeight}px;
  --project-display-sticky-filter-top: 16px;
  --project-display-current-image-height: max(120px, var(--project-display-image-height));
  width: 100%;
  min-width: 0;
  overflow: clip;
  background: transparent;
  color: var(--project-display-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
  container-type: inline-size;
}
.project-display,
.project-display * {
  box-sizing: border-box;
}
.project-display-shell {
  width: 100%;
  max-width: var(--project-display-max-width);
  margin: 0 auto;
  padding: clamp(22px, 2.4vw, 34px);
  border: 1px solid var(--project-display-section-border);
  border-radius: 14px;
  background: var(--project-display-bg);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}
.project-display-header-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: center;
  margin: 0 0 26px;
}
.project-display-header {
  display: grid;
  gap: 10px;
  max-width: 760px;
  margin: 0;
}
.project-display-eyebrow {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  color: var(--project-display-accent);
  font-size: 11px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}
.project-display-eyebrow::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
}
.project-display-title {
  margin: 0;
  color: var(--project-display-text);
  font-size: clamp(34px, 4vw, 54px);
  line-height: 1.08;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.project-display-title-highlight {
  color: var(--project-display-accent);
}
.project-display-description {
  max-width: 680px;
  margin: 0;
  color: var(--project-display-text-soft);
  font-size: 15px;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0;
}
.project-display-action {
  --project-display-action-accent: var(--project-display-accent);
  display: inline-flex;
  min-height: 54px;
  align-items: center;
  justify-content: center;
  gap: 11px;
  border: 1px solid color-mix(in srgb, var(--project-display-action-accent) 48%, var(--project-display-border));
  border-radius: 8px;
  background: color-mix(in srgb, var(--project-display-surface) 92%, var(--project-display-accent-soft));
  color: var(--project-display-action-accent);
  font-size: 14px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  padding: 0 20px;
  text-decoration: none;
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.04);
  transition: transform 170ms ease, box-shadow 170ms ease, border-color 170ms ease;
}
.project-display-action:hover {
  transform: translateY(-1px);
  border-color: var(--project-display-action-accent);
  box-shadow: 0 14px 30px rgba(16, 24, 40, 0.08);
}
.project-display-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--project-display-action-accent) 32%, transparent);
  outline-offset: 3px;
}
.project-display-action-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.project-display-filter-region {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 14px;
  margin: 0 0 28px;
}
.project-display-filter-region--with-search .project-display-filter-bar {
  justify-content: flex-start;
}
.project-display-filter-bar {
  display: flex;
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin: 0;
}
.project-display-search {
  display: flex;
  position: relative;
  width: min(100%, 280px);
  min-width: min(220px, 100%);
  flex: 0 1 280px;
  align-items: center;
}
.project-display-search > .project-display-icon {
  position: absolute;
  z-index: 1;
  left: 14px;
  color: var(--project-display-text-muted);
  pointer-events: none;
}
.project-display-search-input {
  width: 100%;
  min-width: 0;
  height: 42px;
  border: 1px solid var(--project-display-border);
  border-radius: 8px;
  background: var(--project-display-surface);
  color: var(--project-display-text);
  font: inherit;
  font-size: 14px;
  line-height: 1;
  font-weight: 650;
  padding: 0 42px 0 42px;
  box-shadow: 0 7px 18px rgba(16, 24, 40, 0.04);
  transition: border-color 170ms ease, box-shadow 170ms ease;
}
.project-display-search-input::placeholder {
  color: var(--project-display-text-muted);
  opacity: 1;
}
.project-display-search-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}
.project-display-search-input:focus {
  border-color: var(--project-display-accent);
  outline: 3px solid color-mix(in srgb, var(--project-display-accent) 26%, transparent);
  outline-offset: 2px;
}
.project-display-search-clear {
  display: inline-flex;
  position: absolute;
  z-index: 2;
  right: 7px;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--project-display-text-muted);
  cursor: pointer;
  padding: 0;
}
.project-display-search-clear:hover {
  background: var(--project-display-accent-soft);
  color: var(--project-display-accent);
}
.project-display-search-clear:focus-visible {
  outline: 2px solid var(--project-display-accent);
  outline-offset: 1px;
}
.project-display-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
.project-display-mobile-filter-shortcut {
  display: none;
}
.project-display-filter-button {
  --project-display-filter-accent: var(--project-display-accent);
  display: inline-flex;
  min-height: 42px;
  max-width: 100%;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid var(--project-display-border);
  border-radius: 8px;
  background: var(--project-display-surface);
  color: var(--project-display-text);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
  padding: 0 16px;
  text-align: center;
  text-decoration: none;
  box-shadow: 0 7px 18px rgba(16, 24, 40, 0.04);
  transition: transform 170ms ease, border-color 170ms ease, background-color 170ms ease, color 170ms ease, box-shadow 170ms ease;
  white-space: normal;
  overflow-wrap: anywhere;
}
.project-display-filter-button:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--project-display-filter-accent) 32%, var(--project-display-border));
  color: var(--project-display-filter-accent);
}
.project-display-filter-button:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--project-display-filter-accent) 32%, transparent);
  outline-offset: 3px;
}
.project-display-filter-button--active {
  border-color: var(--project-display-filter-accent);
  background: var(--project-display-filter-accent);
  color: #ffffff;
  box-shadow: 0 14px 26px color-mix(in srgb, var(--project-display-filter-accent) 24%, transparent);
}
.project-display-filter-button--active:hover {
  color: #ffffff;
}
.project-display-filter-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.project-display-filter-count {
  display: inline-flex;
  min-width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0 7px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--project-display-filter-accent) 12%, #ffffff);
  color: var(--project-display-filter-accent);
  font-size: 12px;
  line-height: 1;
  font-weight: 900;
}
.project-display-filter-button--active .project-display-filter-count {
  background: rgba(255, 255, 255, 0.95);
  color: var(--project-display-filter-accent);
}
.project-display-track {
  min-width: 0;
}
.project-display-track--slide {
  display: flex;
  align-items: stretch;
  gap: var(--project-display-tile-gap);
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2px 2px 16px;
  scroll-padding-left: 2px;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.project-display-track--stack {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: var(--project-display-tile-gap);
}
.project-display-stack-row {
  display: flex;
  width: 100%;
  align-items: stretch;
  justify-content: center;
  gap: var(--project-display-tile-gap);
}
.project-display-item {
  min-width: 0;
  display: flex;
  align-items: stretch;
}
.project-display-track--stack .project-display-item {
  width: auto;
  max-width: var(--project-display-tile-width);
  flex: 1 1 var(--project-display-min-tile-width);
}
.project-display-track--slide .project-display-item {
  flex: 0 0 min(86vw, var(--project-display-tile-width));
  scroll-snap-align: start;
}
.project-display-empty {
  width: 100%;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--project-display-border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--project-display-surface) 78%, var(--project-display-accent-soft));
  color: var(--project-display-text-muted);
  font-size: 15px;
  line-height: 1.45;
  font-weight: 700;
  letter-spacing: 0;
  padding: 22px;
  text-align: center;
}
.project-display-card {
  --project-display-tile-accent: var(--project-display-accent);
  --project-display-tile-accent-soft: var(--project-display-accent-soft);
  --project-display-sticker-color: var(--project-display-tile-accent);
  --project-display-metric-bg: var(--project-display-metric-surface);
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid var(--project-display-border);
  border-radius: 8px;
  background: var(--project-display-card-bg, var(--project-display-surface));
  color: var(--project-display-text);
  text-decoration: none;
  box-shadow: var(--project-display-shadow);
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}
.project-display-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--project-display-tile-accent) 28%, var(--project-display-border));
  box-shadow: var(--project-display-hover-shadow);
}
.project-display-card::before {
  content: "";
  position: absolute;
  z-index: 2;
  inset: 0 0 auto;
  height: 4px;
  background: var(--project-display-tile-accent);
}
.project-display-card:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--project-display-tile-accent) 34%, transparent);
  outline-offset: 3px;
}
.project-display-media {
  position: relative;
  height: var(--project-display-image-height);
  min-height: 120px;
  overflow: hidden;
  background: var(--project-display-image-fallback);
}
.project-display-image,
.project-display-image-fallback {
  display: block;
  width: 100%;
  height: 100%;
}
.project-display-image {
  object-fit: cover;
  object-position: var(--project-display-image-position, center);
  transform: scale(1);
  transition: transform 420ms ease, filter 420ms ease;
}
.project-display-card:hover .project-display-image {
  transform: scale(1.035);
  filter: saturate(1.04);
}
.project-display-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: var(--project-display-text-muted);
  font-size: 14px;
  line-height: 1.3;
  font-weight: 750;
  text-align: center;
  overflow-wrap: anywhere;
}
.project-display-media::after {
  content: none;
}
.project-display-sticker {
  display: inline-flex;
  position: absolute;
  z-index: 5;
  left: 18px;
  top: calc(var(--project-display-current-image-height) - 20px);
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--project-display-sticker-color) 12%, rgba(16, 24, 40, 0.08));
  border-radius: 999px;
  background: var(--project-display-sticker-bg, rgba(255, 255, 255, 0.86));
  color: var(--project-display-sticker-color);
}
.project-display-sticker .project-display-icon {
  opacity: 0.86;
}
.project-display-metric {
  display: grid;
  position: absolute;
  z-index: 4;
  top: 18px;
  right: 18px;
  min-width: 80px;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid color-mix(in srgb, var(--project-display-tile-accent) 16%, var(--project-display-border));
  border-radius: 8px;
  background: var(--project-display-metric-bg);
  color: var(--project-display-text-soft);
  box-shadow: 0 14px 28px rgba(16, 24, 40, 0.14);
  text-align: center;
}
.project-display-metric-value {
  color: var(--project-display-tile-accent);
  font-size: 18px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.project-display-metric-label {
  color: var(--project-display-text-soft);
  font-size: 11px;
  line-height: 1.2;
  font-weight: 750;
  letter-spacing: 0;
}
.project-display-content {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  padding: 26px 16px 16px;
}
.project-display-icon,
.project-display-icon svg {
  display: block;
  width: var(--project-display-icon-size, 16px);
  height: var(--project-display-icon-size, 16px);
  flex: 0 0 auto;
}
.project-display-icon svg {
  stroke-width: 2.4;
}
.project-display-card-title {
  margin: 0;
  color: var(--project-display-text);
  font-size: 17px;
  line-height: 1.18;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.project-display-card-subtitle {
  margin: 5px 0 0;
  color: var(--project-display-text-soft);
  font-size: 12px;
  line-height: 1.35;
  font-weight: 650;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.project-display-services {
  display: block;
  margin-top: 12px;
}
.project-display-chip-list {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 8px;
}
.project-display-chip {
  --project-display-chip-accent: var(--project-display-tile-accent);
  --project-display-chip-surface: var(--project-display-chip-bg);
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  padding: 7px 10px;
  border-radius: 999px;
  background: var(--project-display-chip-surface);
  color: var(--project-display-chip-text, var(--project-display-text-soft));
  font-size: 11px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.project-display-cta {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 7px;
  margin-top: auto;
  padding-top: 18px;
  color: var(--project-display-tile-accent);
  font-size: 13px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.project-display-cta-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.project-display-cta .project-display-icon {
  transition: transform 180ms ease;
}
.project-display-card:hover .project-display-cta .project-display-icon {
  transform: translateX(3px);
}
.project-display-card--inverse::before {
  content: none;
}
.project-display-card--inverse,
.project-display-card--inverse:hover {
  border-color: transparent;
}
.project-display-card--inverse .project-display-sticker {
  background: var(--project-display-sticker-bg, rgba(255, 255, 255, 0.96));
  color: var(--project-display-sticker-color);
}
.project-display-card--inverse,
.project-display-card--inverse .project-display-card-title,
.project-display-card--inverse .project-display-card-subtitle,
.project-display-card--inverse .project-display-image-fallback,
.project-display-card--inverse .project-display-metric,
.project-display-card--inverse .project-display-metric-label {
  color: #ffffff;
}
.project-display-card--inverse .project-display-chip {
  color: var(--project-display-chip-text, #ffffff);
}
@supports not (color: color-mix(in srgb, red 50%, white)) {
  .project-display-card:hover {
    border-color: var(--project-display-tile-accent);
  }
  .project-display-card:focus-visible {
    outline-color: var(--project-display-tile-accent);
  }
}
@container (min-width: 761px) {
  .project-display-filter-region--sticky {
    position: sticky;
    z-index: 20;
    top: var(--project-display-sticky-filter-top);
    margin-right: -12px;
    margin-left: -12px;
    width: calc(100% + 24px);
    padding: 12px;
    border: 1px solid color-mix(in srgb, var(--project-display-border) 74%, transparent);
    border-radius: 10px;
    background: color-mix(in srgb, var(--project-display-bg) 94%, transparent);
    box-shadow: 0 12px 30px rgba(16, 24, 40, 0.1);
    backdrop-filter: blur(14px);
  }
}
@container (max-width: 760px) {
  .project-display-shell {
    padding: 22px;
  }
  .project-display-header-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .project-display-title {
    font-size: 34px;
  }
  .project-display-description {
    font-size: 14px;
  }
  .project-display-action {
    width: fit-content;
    min-height: 48px;
  }
  .project-display-filter-region {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 22px;
  }
  .project-display-filter-region--sticky {
    position: static;
  }
  .project-display-search {
    width: 100%;
    min-width: 0;
    flex-basis: auto;
  }
  .project-display-filter-bar {
    gap: 10px;
    justify-content: flex-start;
  }
  .project-display-search-input {
    height: 40px;
    font-size: 13px;
  }
  .project-display-filter-button {
    min-height: 40px;
    padding: 0 14px;
    font-size: 13px;
  }
  .project-display-track--slide {
    gap: 14px;
  }
  .project-display-track--stack {
    gap: 14px;
  }
  .project-display-track--stack.project-display-track--responsive-slide {
    flex-direction: row;
    align-items: stretch;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 2px 2px 16px;
    scroll-padding-left: 2px;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  .project-display-stack-row {
    gap: 14px;
  }
  .project-display-track--stack.project-display-track--responsive-slide .project-display-stack-row {
    display: contents;
  }
  .project-display-track--stack.project-display-track--responsive-slide .project-display-item {
    width: auto;
    flex: 0 0 min(86vw, var(--project-display-tile-width));
    scroll-snap-align: start;
  }
  .project-display-card--has-sticker .project-display-sticker {
    position: relative;
    left: auto;
    top: auto;
    align-self: flex-start;
    margin: -19px 0 0 16px;
  }
  .project-display-card--has-sticker .project-display-content {
    padding-top: 12px;
  }
  .project-display-content {
    padding: 24px 14px 14px;
  }
  .project-display-card-title {
    font-size: 16px;
  }
  .project-display-sticker {
    left: 16px;
    top: calc(var(--project-display-current-image-height) - 19px);
    width: 38px;
    height: 38px;
  }
  .project-display-mobile-filter-shortcut--visible {
    display: inline-flex;
    position: fixed;
    z-index: 40;
    right: max(16px, env(safe-area-inset-right));
    bottom: max(16px, env(safe-area-inset-bottom));
    width: 42px;
    height: 42px;
    align-items: center;
    justify-content: center;
    border: 1px solid color-mix(in srgb, var(--project-display-accent) 34%, var(--project-display-border));
    border-radius: 999px;
    background: var(--project-display-accent);
    color: #ffffff;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 12px 28px color-mix(in srgb, var(--project-display-accent) 28%, rgba(16, 24, 40, 0.18));
  }
  .project-display-mobile-filter-shortcut--visible:hover {
    transform: translateY(-1px);
  }
  .project-display-mobile-filter-shortcut--visible:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--project-display-accent) 32%, transparent);
    outline-offset: 3px;
  }
  .project-display-mobile-filter-shortcut-icons {
    display: grid;
    position: relative;
    place-items: center;
    width: 22px;
    height: 22px;
  }
  .project-display-mobile-filter-shortcut-icons > svg:first-child {
    width: 19px;
    height: 19px;
  }
  .project-display-mobile-filter-shortcut-icons > svg:last-child {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 11px;
    height: 11px;
    border-radius: 999px;
    background: var(--project-display-surface);
    color: var(--project-display-accent);
    box-shadow: 0 0 0 2px var(--project-display-accent);
  }
}
@container (max-width: 460px) {
  .project-display {
    --project-display-current-image-height: clamp(120px, var(--project-display-image-height), 132px);
  }
  .project-display-media {
    height: min(var(--project-display-image-height), 132px);
  }
  .project-display-track--slide .project-display-item {
    flex-basis: min(88vw, var(--project-display-tile-width));
  }
  .project-display-track--stack.project-display-track--responsive-slide .project-display-item {
    flex-basis: min(88vw, var(--project-display-tile-width));
  }
  .project-display-title {
    font-size: 30px;
  }
  .project-display-metric {
    top: 14px;
    right: 14px;
    min-width: 72px;
    padding: 10px 12px;
  }
  .project-display-metric-value {
    font-size: 16px;
  }
  .project-display-shell {
    padding: 18px;
  }
}
`;

function toCssSize(value?: number | string) {
    if (typeof value === 'number') return `${value}px`;
    return value;
}

function makeSoftAccent(accent?: string) {
    return accent ? `color-mix(in srgb, ${accent} 12%, transparent)` : undefined;
}

function getImageUrl(image?: ProjectDisplayImage, imageUrl?: string) {
    return imageUrl || image?.src || image?.url || image?.thumbnail || image?.original || '';
}

function getImageAlt(item: ProjectDisplayItem) {
    return (
        item.imageAlt ||
        item.image?.alt ||
        item.image?.thumbnailAlt ||
        item.image?.originalAlt ||
        item.image?.title ||
        (typeof item.title === 'string' ? item.title : 'Project preview')
    );
}

function isServiceChip(service: ProjectDisplayService): service is ProjectDisplayServiceChip {
    return (
        !!service &&
        typeof service === 'object' &&
        !React.isValidElement(service) &&
        'label' in service
    );
}

function getReactNodeText(node: React.ReactNode): string {
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'bigint') {
        return String(node);
    }

    if (Array.isArray(node)) return node.map(getReactNodeText).join(' ');

    if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
        return getReactNodeText(node.props.children);
    }

    return '';
}

function getItemSearchText(item: ProjectDisplayItem) {
    const category = resolveCategory(item);
    const services = item.tags || item.services || [];
    const serviceText = services
        .map((service) => getReactNodeText(isServiceChip(service) ? service.label : service))
        .join(' ');

    return [
        item.searchText,
        getReactNodeText(item.title),
        getReactNodeText(item.subtitle),
        getReactNodeText(item.description),
        getReactNodeText(category?.label),
        serviceText,
        getReactNodeText(item.outcome),
        getReactNodeText(item.outcomeLabel),
    ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase();
}

function itemMatchesTextFilter(item: ProjectDisplayItem, query: string) {
    if (!query) return true;

    const searchableText = getItemSearchText(item);
    return query.split(/\s+/).every((term) => searchableText.includes(term));
}

function renderIcon(
    icon?: React.ReactNode,
    iconToken?: ProjectDisplayIconToken,
    size = 16,
    className = 'project-display-icon',
    iconComponent?: ProjectDisplayIconRenderer,
) {
    const style = { '--project-display-icon-size': `${size}px` } as CSSProperties;

    if (icon) {
        return (
            <span className={className} style={style} aria-hidden="true">
                {icon}
            </span>
        );
    }

    if (iconComponent) {
        const IconComponent = iconComponent;
        return (
            <span className={className} style={style} aria-hidden="true">
                <IconComponent aria-hidden={true} focusable={false} />
            </span>
        );
    }

    if (!iconToken) return null;

    const Icon = iconRegistry[iconToken];
    return (
        <span className={className} style={style} aria-hidden="true">
            <Icon size={size} strokeWidth={2.4} aria-hidden="true" />
        </span>
    );
}

function resolveCategory(item: ProjectDisplayItem): ProjectDisplayCategory | null {
    if (item.category) return item.category;
    if (!item.categoryLabel) return null;

    return {
        label: item.categoryLabel,
        icon: item.categoryIcon,
        iconToken: item.categoryIconToken,
    };
}

function toFilterKey(id: ProjectDisplayFilterId) {
    return String(id);
}

function getFilterableLabelId(label?: React.ReactNode) {
    if (typeof label === 'string' || typeof label === 'number') return String(label);
    return undefined;
}

function getItemFilterKeys(item: ProjectDisplayItem) {
    const category = resolveCategory(item);
    const ids: ProjectDisplayFilterId[] = [];

    if (item.filterId !== undefined) ids.push(item.filterId);
    if (item.filterIds?.length) ids.push(...item.filterIds);

    const categoryLabelId = getFilterableLabelId(category?.label || item.categoryLabel);
    if (categoryLabelId) ids.push(categoryLabelId);

    return ids.map(toFilterKey);
}

function shouldRenderFilterCount(count: React.ReactNode) {
    return count !== undefined && count !== null && count !== false && count !== '';
}

function chunkItems<T>(items: T[], rowSizes: number[]) {
    const rows: T[][] = [];
    let startIndex = 0;

    rowSizes.forEach((rowSize) => {
        rows.push(items.slice(startIndex, startIndex + rowSize));
        startIndex += rowSize;
    });

    return rows;
}

function getBalancedRowSizes(itemCount: number, maxColumns: number) {
    if (itemCount <= 0) return [];

    const columns = Math.max(1, Math.min(itemCount, maxColumns));

    if (columns <= 1) return Array.from({ length: itemCount }, () => 1);
    if (itemCount <= columns) return [itemCount];

    const fullRows = Math.floor(itemCount / columns);
    const remainder = itemCount % columns;

    if (remainder === 0) return Array.from({ length: fullRows }, () => columns);
    if (remainder === 1 && columns > 2) {
        return [
            ...Array.from({ length: Math.max(0, fullRows - 1) }, () => columns),
            columns - 1,
            2,
        ];
    }

    return [...Array.from({ length: fullRows }, () => columns), remainder];
}

function resolveCssLength(element: HTMLElement, value: string) {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    if (trimmedValue.endsWith('px')) {
        const pixelValue = Number.parseFloat(trimmedValue);
        return Number.isFinite(pixelValue) ? pixelValue : null;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue)) return numericValue;

    const probe = document.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.width = trimmedValue;

    element.appendChild(probe);
    const resolvedWidth = probe.getBoundingClientRect().width;
    probe.remove();

    return resolvedWidth > 0 ? resolvedWidth : null;
}

function getStackColumnCount(track: HTMLDivElement, itemCount: number) {
    if (itemCount <= 0) return 1;

    const item = track.querySelector<HTMLElement>('.project-display-item');
    const styles = window.getComputedStyle(track);
    const itemWidth = item?.getBoundingClientRect().width || projectDisplayTokens.sizes.tileWidth;
    const minItemWidth =
        resolveCssLength(track, styles.getPropertyValue('--project-display-min-tile-width')) ||
        Math.min(itemWidth, projectDisplayTokens.sizes.minTileWidth);
    const gap =
        Number.parseFloat(styles.columnGap || styles.gap || '') ||
        Number.parseFloat(styles.gap || '') ||
        0;
    const availableWidths = [
        track.parentElement?.clientWidth,
        track.clientWidth,
        track.getBoundingClientRect().width,
    ].filter((width): width is number => typeof width === 'number' && width > 0);
    const trackWidth = availableWidths.length ? Math.min(...availableWidths) : 0;
    const columnWidth = Math.min(itemWidth, minItemWidth) + gap;
    const measuredColumns =
        columnWidth > 0 ? Math.floor((trackWidth + gap) / columnWidth) : itemCount;

    return Math.max(1, Math.min(itemCount, measuredColumns || 1));
}

function ProjectDisplay({
    items,
    Link = DefaultLink,
    layout = 'slide',
    ResponsiveMode,
    responsiveMode = 'stack',
    eyebrow,
    title,
    titleComponent = 'h2',
    description,
    ctaLabel = 'View Project',
    actionHref,
    actionLabel,
    actionAriaLabel,
    actionIcon,
    actionIconComponent,
    actionIconToken = 'play',
    filters,
    showFilters,
    defaultFilterId,
    allFilterLabel = 'All Projects',
    allFilterCount,
    allFilterIcon,
    allFilterIconComponent,
    allFilterIconToken = 'grid',
    filterAriaLabel = 'Project filters',
    stickyFilters = false,
    stickyFilterTop = 16,
    showTextFilter = false,
    textFilterPlaceholder = 'Search projects...',
    textFilterAriaLabel = 'Search projects',
    clearTextFilterAriaLabel = 'Clear project search',
    mobileFilterShortcutAriaLabel = 'Back to project filters',
    emptyMessage = 'No projects found.',
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    tileWidth,
    minTileWidth,
    tileGap,
    imageHeight,
    className,
    style,
    ariaLabel,
}: ProjectDisplayProps) {
    const mergedColors = { ...colors, ...colours };
    const sourceAccent =
        accentColor || accentColour || mergedColors.accent || projectDisplayTokens.colors.accent;
    const sourceAccentSoft = mergedColors.accentSoft || makeSoftAccent(sourceAccent);
    const resolvedSurface = surfaceColor || surfaceColour || mergedColors.surface;
    const resolvedAccent = sourceAccent;
    const resolvedAccentSoft = sourceAccentSoft;
    const resolvedResponsiveMode = ResponsiveMode || responsiveMode;
    const hasHeader = !!(eyebrow || title || description);
    const hasAction = !!(actionHref && actionLabel);
    const filterOptions = filters || [];
    const shouldShowFilters = showFilters ?? filterOptions.length > 0;
    const hasFilterControls = shouldShowFilters || showTextFilter;
    const [activeFilterId, setActiveFilterId] = useState<ProjectDisplayFilterId | null>(
        defaultFilterId ?? null,
    );
    const [textFilter, setTextFilter] = useState('');
    const [showMobileFilterShortcut, setShowMobileFilterShortcut] = useState(false);
    const sectionRef = useRef<HTMLElement | null>(null);
    const filterRegionRef = useRef<HTMLDivElement | null>(null);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const [stackColumnCount, setStackColumnCount] = useState(1);
    const TitleTag = titleComponent;

    const activeFilterKey =
        activeFilterId !== null && activeFilterId !== undefined
            ? toFilterKey(activeFilterId)
            : null;
    const activeFilterExists =
        shouldShowFilters && activeFilterKey
            ? filterOptions.some((filter) => toFilterKey(filter.id) === activeFilterKey)
            : false;
    const resolvedActiveFilterKey = activeFilterExists ? activeFilterKey : null;
    const normalizedTextFilter = textFilter.trim().toLocaleLowerCase();
    const visibleItems = useMemo(() => {
        return items.filter((item) => {
            const matchesCategory =
                !resolvedActiveFilterKey ||
                getItemFilterKeys(item).includes(resolvedActiveFilterKey);

            return matchesCategory && itemMatchesTextFilter(item, normalizedTextFilter);
        });
    }, [items, normalizedTextFilter, resolvedActiveFilterKey]);

    useEffect(() => {
        trackRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    }, [normalizedTextFilter, resolvedActiveFilterKey]);

    useEffect(() => {
        if (!stickyFilters || !hasFilterControls) {
            setShowMobileFilterShortcut(false);
            return;
        }

        const section = sectionRef.current;
        const filterRegion = filterRegionRef.current;
        if (!section || !filterRegion) return;

        let frameId = 0;
        const updateShortcutVisibility = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(() => {
                const sectionRect = section.getBoundingClientRect();
                const filterRect = filterRegion.getBoundingClientRect();
                const isNarrow = sectionRect.width <= 760;
                const isPastFilters = filterRect.bottom < 8;
                const isWithinSection =
                    sectionRect.bottom > 8 && sectionRect.top < window.innerHeight;

                setShowMobileFilterShortcut(isNarrow && isPastFilters && isWithinSection);
            });
        };

        updateShortcutVisibility();

        const observer =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(updateShortcutVisibility)
                : null;
        observer?.observe(section);
        window.addEventListener('scroll', updateShortcutVisibility, { passive: true });
        window.addEventListener('resize', updateShortcutVisibility);

        return () => {
            window.cancelAnimationFrame(frameId);
            observer?.disconnect();
            window.removeEventListener('scroll', updateShortcutVisibility);
            window.removeEventListener('resize', updateShortcutVisibility);
        };
    }, [hasFilterControls, stickyFilters]);

    useEffect(() => {
        if (layout !== 'stack' || !visibleItems.length) return;

        const track = trackRef.current;
        if (!track) return;

        let frameId = 0;

        const updateColumnCount = () => {
            window.cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(() => {
                const nextColumnCount = getStackColumnCount(track, visibleItems.length);
                setStackColumnCount((currentColumnCount) =>
                    currentColumnCount === nextColumnCount ? currentColumnCount : nextColumnCount,
                );
            });
        };

        updateColumnCount();

        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateColumnCount) : null;
        observer?.observe(track);
        window.addEventListener('resize', updateColumnCount);

        return () => {
            window.cancelAnimationFrame(frameId);
            observer?.disconnect();
            window.removeEventListener('resize', updateColumnCount);
        };
    }, [layout, minTileWidth, tileGap, tileWidth, visibleItems.length]);

    const stackRows = useMemo(() => {
        if (layout !== 'stack') return [];

        return chunkItems(visibleItems, getBalancedRowSizes(visibleItems.length, stackColumnCount));
    }, [layout, stackColumnCount, visibleItems]);

    const sectionStyle: ProjectDisplayStyle = {
        ...style,
        '--project-display-bg': backgroundColor || backgroundColour || mergedColors.background,
        '--project-display-surface': resolvedSurface,
        '--project-display-text': textColor || textColour || mergedColors.text,
        '--project-display-text-soft': mergedColors.textSoft,
        '--project-display-text-muted': mergedColors.textMuted,
        '--project-display-accent': resolvedAccent,
        '--project-display-accent-soft': resolvedAccentSoft,
        '--project-display-border': mergedColors.border,
        '--project-display-section-border': mergedColors.sectionBorder,
        '--project-display-chip-bg': mergedColors.chipBackground,
        '--project-display-image-fallback': mergedColors.imageFallback,
        '--project-display-metric-surface': mergedColors.metricSurface,
        '--project-display-shadow': mergedColors.shadow,
        '--project-display-hover-shadow': mergedColors.hoverShadow,
        '--project-display-max-width': toCssSize(maxWidth),
        '--project-display-tile-width': toCssSize(tileWidth),
        '--project-display-min-tile-width': toCssSize(minTileWidth),
        '--project-display-tile-gap': toCssSize(tileGap),
        '--project-display-image-height': toCssSize(imageHeight),
        '--project-display-sticky-filter-top': toCssSize(stickyFilterTop),
    };

    const renderProjectItem = (item: ProjectDisplayItem) => {
        const category = resolveCategory(item);
        const imageUrl = getImageUrl(item.image, item.imageUrl);
        const cardTags = item.tags || item.services || [];
        const itemMetric = item.metric || item.stat;
        const itemSticker = item.sticker ?? item.overlaySticker;
        const stickerIcon = itemSticker?.icon || item.overlayIcon;
        const stickerIconComponent = itemSticker?.iconComponent || item.overlayIconComponent;
        const stickerIconToken = itemSticker?.iconToken || item.overlayIconToken;
        const hasSticker = !!(
            itemSticker !== null &&
            (stickerIcon || stickerIconComponent || stickerIconToken)
        );
        const itemInverse = !!item.inverse;
        const itemSubtitle =
            item.subtitle || category?.label || item.categoryLabel || item.description;
        const sourceItemAccent =
            item.accentColor ||
            item.accentColour ||
            category?.accentColor ||
            category?.accentColour ||
            sourceAccent;
        const sourceItemAccentSoft =
            item.accentSoftColor ||
            item.accentSoftColour ||
            category?.accentSoftColor ||
            category?.accentSoftColour ||
            makeSoftAccent(sourceItemAccent) ||
            resolvedAccentSoft;
        const itemAccent = itemInverse ? projectDisplayInverseColors.text : sourceItemAccent;
        const itemAccentSoft = itemInverse
            ? projectDisplayInverseColors.accentSoft
            : sourceItemAccentSoft;
        const stickerBackground = itemSticker?.backgroundColor || itemSticker?.backgroundColour;
        const itemStyle: ProjectDisplayItemStyle = {
            '--project-display-card-bg': itemInverse ? sourceItemAccent : undefined,
            '--project-display-tile-accent': itemAccent,
            '--project-display-tile-accent-soft': itemAccentSoft,
            '--project-display-sticker-color': itemInverse
                ? itemSticker?.color || itemSticker?.colour || sourceItemAccent
                : itemSticker?.color || itemSticker?.colour || stickerBackground || itemAccent,
            '--project-display-sticker-bg': itemInverse
                ? stickerBackground || projectDisplayInverseColors.stickerBackground
                : undefined,
            '--project-display-metric-bg': itemInverse ? sourceItemAccent : undefined,
            '--project-display-image-position':
                item.imagePosition || item.image?.objectPosition || undefined,
        };

        return (
            <div className="project-display-item" key={item.id}>
                <Link
                    href={item.href}
                    className={[
                        'project-display-card',
                        itemInverse ? 'project-display-card--inverse' : '',
                        hasSticker ? 'project-display-card--has-sticker' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    style={itemStyle}
                    aria-label={
                        item.ctaAriaLabel ||
                        (typeof item.title === 'string' ? `View project: ${item.title}` : undefined)
                    }
                >
                    <div className="project-display-media">
                        {imageUrl ? (
                            <img
                                className="project-display-image"
                                src={imageUrl}
                                alt={getImageAlt(item)}
                                loading="lazy"
                            />
                        ) : (
                            <div className="project-display-image-fallback">{item.title}</div>
                        )}

                        {itemMetric ? (
                            <div
                                className="project-display-metric"
                                aria-label={itemMetric.ariaLabel}
                            >
                                <span className="project-display-metric-value">
                                    {itemMetric.value}
                                </span>
                                {itemMetric.label ? (
                                    <span className="project-display-metric-label">
                                        {itemMetric.label}
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {hasSticker ? (
                        <span
                            className="project-display-sticker"
                            aria-label={itemSticker?.ariaLabel}
                            aria-hidden={itemSticker?.ariaLabel ? undefined : true}
                        >
                            {renderIcon(
                                stickerIcon,
                                stickerIconToken,
                                19,
                                'project-display-icon',
                                stickerIconComponent,
                            )}
                        </span>
                    ) : null}

                    <div className="project-display-content">
                        <h3 className="project-display-card-title">{item.title}</h3>

                        {itemSubtitle ? (
                            <p className="project-display-card-subtitle">{itemSubtitle}</p>
                        ) : null}

                        {cardTags.length ? (
                            <div className="project-display-services">
                                <div className="project-display-chip-list">
                                    {cardTags.map((service, index) => {
                                        const chip: ProjectDisplayServiceChip = isServiceChip(
                                            service,
                                        )
                                            ? service
                                            : { label: service };
                                        const chipTextColor =
                                            chip.color ||
                                            chip.colour ||
                                            chip.textColor ||
                                            chip.textColour ||
                                            chip.accentColor ||
                                            chip.accentColour;
                                        const chipBackground =
                                            chip.backgroundColor || chip.backgroundColour;
                                        const chipStyle = {
                                            '--project-display-chip-accent': chipTextColor,
                                            '--project-display-chip-text':
                                                chipTextColor ||
                                                (itemInverse
                                                    ? projectDisplayInverseColors.text
                                                    : undefined),
                                            '--project-display-chip-surface':
                                                chipBackground ||
                                                (itemInverse
                                                    ? projectDisplayInverseColors.chipBackground
                                                    : undefined),
                                        } as CSSProperties;

                                        return (
                                            <span
                                                className="project-display-chip"
                                                style={chipStyle}
                                                aria-label={chip.ariaLabel}
                                                key={index}
                                            >
                                                {chip.label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}

                        <span className="project-display-cta">
                            <span className="project-display-cta-text">
                                {item.ctaLabel || ctaLabel}
                            </span>
                            {renderIcon(undefined, 'arrowRight', 16)}
                        </span>
                    </div>
                </Link>
            </div>
        );
    };

    const scrollToFilters = () => {
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        filterRegionRef.current?.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    return (
        <section
            ref={sectionRef}
            className={['project-display', className].filter(Boolean).join(' ')}
            style={sectionStyle}
            aria-label={ariaLabel}
        >
            <style>{projectDisplayCss}</style>

            <div className="project-display-shell">
                {hasHeader || hasAction ? (
                    <div className="project-display-header-row">
                        {hasHeader ? (
                            <header className="project-display-header">
                                {eyebrow ? (
                                    <p className="project-display-eyebrow">{eyebrow}</p>
                                ) : null}
                                {title ? (
                                    <TitleTag className="project-display-title">{title}</TitleTag>
                                ) : null}
                                {description ? (
                                    <p className="project-display-description">{description}</p>
                                ) : null}
                            </header>
                        ) : (
                            <span aria-hidden="true" />
                        )}

                        {hasAction ? (
                            <Link
                                href={actionHref || '#'}
                                className="project-display-action"
                                aria-label={actionAriaLabel}
                            >
                                {renderIcon(
                                    actionIcon,
                                    actionIconToken,
                                    18,
                                    'project-display-icon',
                                    actionIconComponent,
                                )}
                                <span className="project-display-action-text">{actionLabel}</span>
                                {renderIcon(undefined, 'arrowRight', 16)}
                            </Link>
                        ) : null}
                    </div>
                ) : null}

                {hasFilterControls ? (
                    <div
                        ref={filterRegionRef}
                        className={[
                            'project-display-filter-region',
                            stickyFilters ? 'project-display-filter-region--sticky' : '',
                            showTextFilter ? 'project-display-filter-region--with-search' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {showTextFilter ? (
                            <div className="project-display-search">
                                {renderIcon(undefined, 'search', 17)}
                                <input
                                    className="project-display-search-input"
                                    type="search"
                                    value={textFilter}
                                    placeholder={textFilterPlaceholder}
                                    aria-label={textFilterAriaLabel}
                                    autoComplete="off"
                                    onChange={(event) => setTextFilter(event.currentTarget.value)}
                                />
                                {textFilter ? (
                                    <button
                                        type="button"
                                        className="project-display-search-clear"
                                        aria-label={clearTextFilterAriaLabel}
                                        onClick={() => setTextFilter('')}
                                    >
                                        <X size={16} strokeWidth={2.4} aria-hidden="true" />
                                    </button>
                                ) : null}
                            </div>
                        ) : null}

                        {shouldShowFilters ? (
                            <div
                                className="project-display-filter-bar"
                                role="group"
                                aria-label={filterAriaLabel}
                            >
                                <button
                                    type="button"
                                    className={[
                                        'project-display-filter-button',
                                        !resolvedActiveFilterKey
                                            ? 'project-display-filter-button--active'
                                            : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    style={
                                        {
                                            '--project-display-filter-accent': resolvedAccent,
                                        } as CSSProperties
                                    }
                                    aria-pressed={!resolvedActiveFilterKey}
                                    onClick={() => setActiveFilterId(null)}
                                >
                                    {renderIcon(
                                        allFilterIcon,
                                        allFilterIconToken,
                                        16,
                                        'project-display-icon',
                                        allFilterIconComponent,
                                    )}
                                    <span className="project-display-filter-text">
                                        {allFilterLabel}
                                    </span>
                                    {shouldRenderFilterCount(allFilterCount) ? (
                                        <span className="project-display-filter-count">
                                            {allFilterCount}
                                        </span>
                                    ) : null}
                                </button>

                                {filterOptions.map((filter) => {
                                    const filterKey = toFilterKey(filter.id);
                                    const isActive = resolvedActiveFilterKey === filterKey;
                                    const filterAccent =
                                        filter.accentColor || filter.accentColour || resolvedAccent;

                                    return (
                                        <button
                                            type="button"
                                            className={[
                                                'project-display-filter-button',
                                                isActive
                                                    ? 'project-display-filter-button--active'
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            style={
                                                {
                                                    '--project-display-filter-accent': filterAccent,
                                                } as CSSProperties
                                            }
                                            aria-label={filter.ariaLabel}
                                            aria-pressed={isActive}
                                            key={filterKey}
                                            onClick={() => setActiveFilterId(filter.id)}
                                        >
                                            {renderIcon(
                                                filter.icon,
                                                filter.iconToken,
                                                16,
                                                'project-display-icon',
                                                filter.iconComponent,
                                            )}
                                            <span className="project-display-filter-text">
                                                {filter.label}
                                            </span>
                                            {shouldRenderFilterCount(filter.count) ? (
                                                <span className="project-display-filter-count">
                                                    {filter.count}
                                                </span>
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}

                        <span
                            className="project-display-visually-hidden"
                            role="status"
                            aria-live="polite"
                        >
                            {visibleItems.length}{' '}
                            {visibleItems.length === 1 ? 'project shown' : 'projects shown'}
                        </span>
                    </div>
                ) : null}

                {visibleItems.length ? (
                    <div
                        ref={trackRef}
                        className={[
                            'project-display-track',
                            `project-display-track--${layout}`,
                            `project-display-track--responsive-${resolvedResponsiveMode}`,
                        ].join(' ')}
                    >
                        {layout === 'stack'
                            ? stackRows.map((row, rowIndex) => (
                                  <div className="project-display-stack-row" key={rowIndex}>
                                      {row.map(renderProjectItem)}
                                  </div>
                              ))
                            : visibleItems.map(renderProjectItem)}
                    </div>
                ) : (
                    <div className="project-display-empty">{emptyMessage}</div>
                )}
            </div>

            {stickyFilters && hasFilterControls ? (
                <button
                    type="button"
                    className={[
                        'project-display-mobile-filter-shortcut',
                        showMobileFilterShortcut
                            ? 'project-display-mobile-filter-shortcut--visible'
                            : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    aria-label={mobileFilterShortcutAriaLabel}
                    onClick={scrollToFilters}
                >
                    <span
                        className="project-display-mobile-filter-shortcut-icons"
                        aria-hidden="true"
                    >
                        <ListFilter strokeWidth={2.4} />
                        <ArrowUp strokeWidth={3} />
                    </span>
                </button>
            ) : null}
        </section>
    );
}

export default ProjectDisplay;
